import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { type ApiKeyCreated, apiKeysApi } from '@/api/api-keys.api';
import { formatApiMessage } from '@/api/client';
import { AppShell } from '@/components/layout/AppShell';
import { formatDateTime } from '@/lib/format';

export function IntegrationsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState<ApiKeyCreated | null>(null);

  const { data: keys = [] } = useQuery({
    queryKey: ['api-keys', 'list'],
    queryFn: () => apiKeysApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => apiKeysApi.create({ name }),
    onSuccess: (data) => {
      setNewKey(data);
      setShowCreate(false);
      qc.invalidateQueries({ queryKey: ['api-keys', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const revokeMutation = useMutation({
    mutationFn: (uuid: string) => apiKeysApi.revoke(uuid),
    onSuccess: () => {
      toast.success(t('settings_integrations.revoked'));
      qc.invalidateQueries({ queryKey: ['api-keys', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="page-title">{t('settings_integrations.title')}</h1>
        <p className="page-subtitle">
          {t('settings_integrations.subtitle')}{' '}
          <a href="/api/docs" target="_blank" rel="noreferrer" className="text-primary">/api/docs</a>.
        </p>
      </div>

      <div className="card mb-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900 m-0">{t('settings_integrations.api_keys')}</h3>
            <p className="text-[12px] text-muted mt-0.5">
              {t('settings_integrations.usage_hint')}
            </p>
          </div>
          <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm">
            {t('settings_integrations.new_key_btn')}
          </button>
        </div>

        {keys.length === 0 ? (
          <div className="text-center py-8 text-muted text-[13px]">
            {t('settings_integrations.no_keys_text')}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('settings_integrations.th_name')}</th>
                <th>{t('settings_integrations.th_prefix')}</th>
                <th>{t('settings_integrations.th_created')}</th>
                <th>{t('settings_integrations.th_last_used')}</th>
                <th>{t('common.status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className={k.revoked ? 'opacity-50' : ''}>
                  <td className="font-medium text-gray-900">{k.name}</td>
                  <td><code className="text-[12px]">{k.prefix}…</code></td>
                  <td className="text-[12px] text-muted">{formatDateTime(k.createdAt)}</td>
                  <td className="text-[12px] text-muted">
                    {k.lastUsedAt ? formatDateTime(k.lastUsedAt) : t('settings_integrations.never_used')}
                    {k.lastUsedIp && <div className="text-[11px]">{k.lastUsedIp}</div>}
                  </td>
                  <td>
                    <span className={`badge ${k.revoked ? 'badge-danger' : 'badge-success'} text-[10px]`}>
                      {k.revoked ? t('settings_integrations.status_revoked') : t('settings_integrations.status_active')}
                    </span>
                  </td>
                  <td className="text-right">
                    {!k.revoked && (
                      <button
                        onClick={() => {
                          if (confirm(t('settings_integrations.revoke_confirm', { name: k.name }))) {
                            revokeMutation.mutate(k.id);
                          }
                        }}
                        className="btn btn-ghost btn-sm text-danger"
                      >
                        {t('settings_integrations.revoke')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateKeyModal
          onClose={() => setShowCreate(false)}
          onCreate={(name) => createMutation.mutate(name)}
          pending={createMutation.isPending}
        />
      )}

      {newKey && (
        <NewKeyModal apiKey={newKey} onClose={() => setNewKey(null)} />
      )}
    </AppShell>
  );
}

function CreateKeyModal({
  onClose,
  onCreate,
  pending,
}: {
  onClose: () => void;
  onCreate: (name: string) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t('settings_integrations.new_key_btn')}</h2>
          <button onClick={onClose} className="text-muted hover:text-gray-900 text-xl leading-none">×</button>
        </div>
        <div className="p-5">
          <label className="label">{t('settings_integrations.key_name')}</label>
          <input
            className="input"
            placeholder={t('settings_integrations.key_name_placeholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <p className="text-[12px] text-muted mt-2">{t('settings_integrations.create_hint')}</p>
        </div>
        <div className="p-5 border-t border-border flex justify-end gap-2">
          <button onClick={onClose} className="btn btn-secondary">{t('common.cancel')}</button>
          <button
            onClick={() => onCreate(name.trim() || t('settings_integrations.untitled_key'))}
            disabled={pending}
            className="btn btn-primary"
          >
            {pending ? t('common.creating') : t('settings_integrations.create')}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewKeyModal({ apiKey, onClose }: { apiKey: ApiKeyCreated; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-xl">
        <div className="p-5 border-b border-border">
          <h2 className="text-lg font-semibold">{t('settings_integrations.save_key_title')}</h2>
          <p className="text-[12px] text-muted mt-1">{t('settings_integrations.show_once')}</p>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="label text-muted">{t('settings_integrations.key_name')}</label>
            <div className="font-medium">{apiKey.name}</div>
          </div>
          <div>
            <label className="label text-muted">{t('settings_integrations.token_label')}</label>
            <div className="bg-gray-50 border border-border rounded-md p-3 font-mono text-[12px] break-all">
              {apiKey.rawToken}
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(apiKey.rawToken);
                toast.success(t('common.copied'));
              }}
              className="btn btn-secondary btn-sm mt-2"
            >
              {t('common.copy')}
            </button>
          </div>
          <div className="bg-warning-bg border border-warning text-[12px] text-warning-fg rounded-md p-3">
            <strong>{t('settings_integrations.important')}:</strong> {t('settings_integrations.token_warning')}
          </div>
        </div>
        <div className="p-5 border-t border-border flex justify-end">
          <button onClick={onClose} className="btn btn-primary">{t('settings_integrations.done')}</button>
        </div>
      </div>
    </div>
  );
}
